import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SaveQuoteModalComponent } from '../../save-quote-modal/save-quote-modal.component';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { SaveQuoteMetaData, SaveQuoteModalArgs } from '../interfaces/save-quote-data.interface';
import { DefaultFormConfig, FormConfig, FormNavigationConfig } from '@ptl/forms';
import { ApplicationRecord } from '../../interfaces/application-record.interface';
import { IndividualMarketSaveQuoteUtilityService } from './individual-market-save-quote-utility.service';
import { SaveQuoteModalConfigurationData } from '../../save-quote-modal/interfaces/save-quote-modal.interface';

@Injectable({
  providedIn: 'root'
})
export class SaveQuoteModalTriggerService {

  constructor(
    private matDialog: MatDialog,
    private ngRedux: NgRedux<StateStructure>,
    private individualMarketSaveQuoteUtilityService: IndividualMarketSaveQuoteUtilityService
  ) { }

  openSaveQuoteModal(openSaveQuoteModalArgs: SaveQuoteModalConfigurationData) {

    // We save this inside of the modal, doesn't appear to be a reason we'd need to save this value only when modal is opened
    // if (formConfigs.enrollmentFlow){
    //   this.ngRedux.dispatch(saveEnrollmentFlow(formConfigs.enrollmentFlow));
    // }

    return this.matDialog.open(SaveQuoteModalComponent, {
      maxWidth: "50vw",
      data: openSaveQuoteModalArgs
    })
  }

  /** 
   * Use this when we have to compare against existing saved data
   * Specifically for flows bouncing between producer and consumer - maintain the original info
   * 
   * SPECIFICALLY FOR USE WITH THE JSON FORMS ( you can manually set these values when saving outside of the forms-as-json scenario )
   */
  getSaveQuoteModalArgs(formConfig: DefaultFormConfig): SaveQuoteModalArgs {
    
    /** INNER FUNCTIONS */
    const _useActiveFormConfigModalArgs = () => {
      /** 
       * Saving, but no historical saved-formConfig data
       * i.e. producer resuming flow will have these values on their current form
       **/
      return {
        urls: {
          returnUrl: formConfig.form.customConfigs?.saveForLaterPageUrl,
          saveForConsumerDocumentUploadPageUrl: formConfig.form.customConfigs?.saveForConsumerDocumentUploadPageUrl
        },
        enrollmentFlow: formConfig.form.customConfigs?.enrollmentFlow
      };
    }
    const _useSavedModalArgs = (savedData: SaveQuoteMetaData) => {
      /**
       * Saving, but WITH historical saved-formConfig data
       * i.e. consumer doing referral will have been provided these values; producer resuming app will have been provided these values
       **/
      return {
        urls: savedData.urls,
        enrollmentFlow: this.individualMarketSaveQuoteUtilityService.getSavedQuoteStateData().enrollmentFlow
      }

    }

    /**
     * Sometimes we'll save data and resume the application (producer -> consumer)
     * Sometimes we'll resume the application and the form configuration won't contain the same data (producer:enroll -> producer:plan-details)
     * i.e. TELESALES user sends CONSUMER user an application which the consumer must update without changing underlying agent-configs
     * e.g. document upload for telesales users ( who themselves cannot receive consumer documentation )
     * 
     * This also supports a scenario where a producer is updating an existing quote, but saving it from a different page.
     * The last save location will drive the returnUrl value ( so you save on plan details, that's where you'll return )
     */

    const savedData = this.individualMarketSaveQuoteUtilityService.getSavedQuoteConfigsFromResumedApp();
    
    if (!savedData) {
      return _useActiveFormConfigModalArgs(); // Nothing already present, use new stuff
    } else {
      const prevSavedInfo = _useSavedModalArgs(savedData); 
      const saveInfo = Object.assign({}, prevSavedInfo); // If prev save info exists, use it as base

      const currentPageSaveInfo = _useActiveFormConfigModalArgs();
      if (currentPageSaveInfo.urls) { // If current page has URLs, use that ( example, you saved on plan details, now are resaving on enrollment, get new url value )
        saveInfo.urls = currentPageSaveInfo.urls;
      }
      return saveInfo;
    }

  }
}
